import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/userrevenue/Data Table/index";

export default function UserRevenue() {
  const title = "User Revenue Summary";

  const [apiData, setApiData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalUsers: 0,
  });
  const [totalPayout, setTotalPayout] = useState(0);
  const [totalTrips, setTotalTrips] = useState(0);
  const [totalFixedTrips, setTotalFixedTrips] = useState(0);
  const [totalPpTrips, setTotalPpTrips] = useState(0);
  const [dateTotals, setDateTotals] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");

  const selectedMonth = moment().month() + 1;
  const selectedYear = moment().year();
  const fetchData = async (
    page = 1,
    month = selectedMonth,
    year = selectedYear,
    search = "",
    limit = pagination.limit,
    from = fromDate,
    to = toDate,
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/user-payout/summary`,
        {
          params: {
            page,
            limit,
            search,

            ...(from && to
              ? {
                  fromDate: from,
                  toDate: to,
                }
              : {
                  month,
                  year,
                }),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.success) return;

      const payoutSummary = response.data.payoutSummary || {};
      const userSummary = payoutSummary.userSummary || [];
      const apiDateTotals = payoutSummary.dateTotals || [];

      setDateTotals(apiDateTotals);

      /* ✅ SET DATA */
      setApiData(userSummary);
      setPagination(response.data.pagination || {});
      setTotalPayout(payoutSummary.totalPayout || 0);
      setTotalTrips(payoutSummary.totalTrips || 0);
      setTotalFixedTrips(payoutSummary.fixedTrips || 0);
      setTotalPpTrips(payoutSummary.ppTrips || 0);

      /* 🔹 COLLECT UNIQUE DATES */
      const dateSet = new Set();
      userSummary.forEach((user) => {
        Object.keys(user.dateSummary || {}).forEach((date) =>
          dateSet.add(date),
        );
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      /* 🔹 STATIC COLUMNS */
      const staticColumns = [
        {
          name: <b>User Name</b>,
          selector: (row) => row.name || "-",
          sortable: true,
          minWidth: "150px",
        },
        {
          name: <b>Phone Number</b>,
          selector: (row) => String(row.phonenumber || "-"),
          sortable: true,
          minWidth: "150px",
        },
        {
          name: (
            <div
              style={{
                color: "#f8d7da",
                fontWeight: "700",
                fontSize: "14px",
                background: "#dc3545",
                padding: "4px 10px",
                borderRadius: "6px",
                minWidth: "70px",
                textAlign: "center",
                display: "inline-block",
              }}
            >
              Cash Short
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                ₹{(payoutSummary?.totalCashSort || 0).toLocaleString()}
              </div>
            </div>
          ),
          selector: (row) => row.cashSort,
          center: true,
          cell: (row) => (
            <span
              style={{
                color: "#dc3545",
                fontWeight: 600,
                background: "#f8d7da",
                padding: "4px 10px",
                borderRadius: "6px",
                minWidth: "70px",
                textAlign: "center",
                display: "inline-block",
              }}
            >
              {(row.cashSort || 0).toLocaleString()}
            </span>
          ),
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#0d6efd",
                color: "#fff",
                padding: "6px 10px",
                border: "1px solid #084298",
                borderRadius: "4px",
                width: "100%",
                textAlign: "center",
              }}
            >
              {/* <b>Total Trips ({payoutSummary.totalTrips})</b> */}
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total Trips: ({payoutSummary.totalTrips || 0})
              </div>
            </div>
          ),
          selector: (row) => row.trips,
          center: true,
        },
        {
          name: (
            <div
              style={{
                backgroundColor: "#0d6efd",
                color: "#fff",
                padding: "6px 10px",
                border: "1px solid #084298",
                borderRadius: "4px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total Payout: ₹
                {Number(payoutSummary.totalPayout || 0).toLocaleString()}
              </div>{" "}
            </div>
          ),
          selector: (row) => Number(row.payout || 0).toFixed(2),
          center: true,
        },
        {
          name: <b>Fixed Trips ({payoutSummary.fixedTrips})</b>,
          selector: (row) => row.fixedTrips || "-",
          center: true,
        },
        {
          name: <b>PP Trips ({payoutSummary.ppTrips})</b>,
          selector: (row) => row.ppTrips || "-",
          center: true,
        },
        {
          name: (
            <b>
              Fixed Payout ({" "}
              {Number(payoutSummary.totalFixedPayout || 0).toLocaleString()})
            </b>
          ),
          selector: (row) => Number(row.fixedPayout || 0).toFixed(2),
          center: true,
        },
        {
          name: (
            <b>
              PP Payout ({" "}
              {Number(payoutSummary.totalPPPayout || 0).toLocaleString()})
            </b>
          ),
          selector: (row) => Number(row.ppPayout || 0).toFixed(2),
          center: true,
        },
      ];

      /* 🔹 DYNAMIC DATE COLUMNS (FIXED ✅) */
      const dynamicColumns = [];

      sortedDates.forEach((date, index) => {
        const headerBg = index % 2 === 0 ? "#eef4ff" : "#f9fafb";
        const totalsForDate = apiDateTotals.find((d) => d.date === date);

        const headerStyle = {
          backgroundColor: headerBg,
          padding: "6px 4px",
          borderRadius: "4px",
          textAlign: "center",
        };

        // Trips column
        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                Fixed Trips: {totalsForDate?.fixedTrips || 0}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) => Number(row.dateSummary?.[date]?.fixedTrips || 0),
        });
        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                PP Trips: {totalsForDate?.ppTrips || 0}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) => Number(row.dateSummary?.[date]?.ppTrips || 0),
        });
        // Payout column
        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                Fixed Payout: ₹
                {Number(totalsForDate?.fixedAmount || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.fixedPayout || 0).toFixed(2),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px" }}>
                PP Payout: ₹
                {Number(totalsForDate?.ppAmount || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.ppPayout || 0).toFixed(2),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total Trips: {totalsForDate?.trips || 0}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) => Number(row.dateSummary?.[date]?.trips || 0),
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              {/* <div style={{ fontSize: "12px" }}>Total</div> */}
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                Total: ₹
                {Number(totalsForDate?.totalAmount || 0).toLocaleString()}
              </div>
              <b>{moment(date).format("D MMM YYYY")}</b>
            </div>
          ),
          center: true,
          minWidth: "120px",
          selector: (row) =>
            Number(row.dateSummary?.[date]?.payout || 0).toFixed(2),
        });
      });

      /* ✅ FINAL COLUMN SET */
      setColumns([...staticColumns, ...dynamicColumns]);
    } catch (error) {
      console.error("User Revenue Error:", error);
    }
  };

  /* ✅ INITIAL LOAD */
  useEffect(() => {
    fetchData(1);
  }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found");
        return;
      }

      if (!exportFromDate || !exportToDate) {
        alert("Please select both From Date and To Date");
        return;
      }

      if (new Date(exportFromDate) > new Date(exportToDate)) {
        alert("From Date cannot be greater than To Date");
        return;
      }

      setLoadingExport(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/user-payout/summary`,
        {
          params: {
            page: 1,
            limit: 100000,
            fromDate: exportFromDate,
            toDate: exportToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data?.success) {
        console.error("Export API failed");
        return;
      }

      const payoutSummary = response.data.payoutSummary || {};
      const userSummary = payoutSummary.userSummary || [];

      if (!userSummary.length) {
        alert("No data found for the selected date range");
        return;
      }

      // Collect all unique dates
      const dateSet = new Set();

      userSummary.forEach((user) => {
        Object.keys(user.dateSummary || {}).forEach((date) => {
          dateSet.add(date);
        });
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      // Build Excel rows
      const transformedData = userSummary.map((user, index) => {
        const row = {
          "S.No": index + 1,
          "User Name": user.name || "-",
          "Phone Number": user.phonenumber || "-",
          "Cash Short": user.cashSort || 0,
          "Total Trips": user.trips || 0,
          "Total Payout": Number(user.payout || 0).toFixed(2),
          "Fixed Trips": user.fixedTrips || 0,
          "PP Trips": user.ppTrips || 0,
          "Fixed Payout": Number(user.fixedPayout || 0).toFixed(2),
          "PP Payout": Number(user.ppPayout || 0).toFixed(2),
        };

        // Add date-wise columns
        sortedDates.forEach((date) => {
          const summary = user.dateSummary?.[date] || {};

          const formattedDate = moment(date).format("D MMM YYYY");

          row[`${formattedDate} - Fixed Trips`] = summary.fixedTrips || 0;

          row[`${formattedDate} - PP Trips`] = summary.ppTrips || 0;

          row[`${formattedDate} - Fixed Payout`] = Number(
            summary.fixedPayout || 0,
          ).toFixed(2);

          row[`${formattedDate} - PP Payout`] = Number(
            summary.ppPayout || 0,
          ).toFixed(2);

          row[`${formattedDate} - Total Trips`] = summary.trips || 0;

          row[`${formattedDate} - Total Payout`] = Number(
            summary.payout || 0,
          ).toFixed(2);
        });

        return row;
      });

      // Create Excel
      const worksheet = XLSX.utils.json_to_sheet(transformedData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "User Revenue");

      XLSX.writeFile(
        workbook,
        `user_revenue_${exportFromDate}_to_${exportToDate}.xlsx`,
      );

      setShowExportModal(false);
    } catch (error) {
      console.error(
        "Error exporting User Revenue:",
        error.response?.data || error.message,
      );
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              {/* <div className="card-header">
                <h4>{title}</h4>
              </div> */}
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{title}</h4>

                <button
                  className="btn btn-success btn-sm text-white"
                  onClick={() => setShowExportModal(true)}
                >
                  Export to Excel
                </button>
              </div>
              {showExportModal && (
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Select Date Range</h5>

                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowExportModal(false)}
                        ></button>
                      </div>

                      <div className="modal-body d-flex flex-column gap-3">
                        <div>
                          <label>From Date:</label>

                          <input
                            type="date"
                            className="form-control"
                            value={exportFromDate}
                            onChange={(e) => setExportFromDate(e.target.value)}
                          />
                        </div>

                        <div>
                          <label>To Date:</label>

                          <input
                            type="date"
                            className="form-control"
                            value={exportToDate}
                            onChange={(e) => setExportToDate(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowExportModal(false)}
                        >
                          Cancel
                        </button>

                        <button
                          className="btn btn-success"
                          onClick={handleExport}
                          disabled={loadingExport}
                        >
                          {loadingExport ? "Please wait..." : "Export"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="card-body">
                <DataTable_Component
                  columns={columns}
                  fetchData={fetchData}
                  totalPayout={totalPayout}
                  totalTrips={totalTrips}
                  totalFixedTrips={totalFixedTrips}
                  totalPpTrips={totalPpTrips}
                  paginationData={{
                    data: apiData,
                    page: pagination.page,
                    limit: pagination.limit,
                    totalUsers: pagination.totalUsers, // 🔹 matches component API
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
